import { useRef, useState } from "react";

import { MayString, doNothing } from "@bridgesplit/utils";
import { Skeleton, keyframes, styled } from "@mui/material";

import { MediaQuery, SxType } from "../types";
import { BORDER_RADIUS, SPACING, useAppPalette } from "../theme";
import { Row } from "./containers";
import { MEDIA } from "../util";
import { Text, TextVariant } from "./typography";

const StyledImage = styled("img")(() => ({}));

// Srcs loaded this session, so re-encountered icons skip the skeleton flash.
const loadedSrcs = new Set<string>();

const pulse = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
`;

type ImageVariant = "circle" | "square" | "rounded" | "fixed-width" | "fixed-height";
interface BaseImageProps {
    src: MayString;
    sx?: SxType;
    mobileSx?: SxType;
    loading?: boolean;
    hideSkeleton?: boolean;
    border?: boolean;
    variant?: ImageVariant;
    skeletonVariant?: "circular" | "rectangular";
    skeletonSx?: SxType;

    mobileWidth?: string;
    mobileHeight?: string;
    query?: MediaQuery;
}
// force user to specify a width and height if illustration, otherwise specify just a size
interface ImageWithSize extends BaseImageProps {
    size: string;
}
interface ImageWithHeight extends BaseImageProps {
    variant: "fixed-height";
    height: string;
    width?: string;
}
interface ImageWithWidth extends BaseImageProps {
    variant: "fixed-width";
    width: string;
    height?: string;
}
export type ImageProps = ImageWithSize | ImageWithHeight | ImageWithWidth;

export function Image({
    variant = "rounded",
    sx,
    mobileSx,
    src,
    loading,
    hideSkeleton,
    border: showBorder,
    mobileHeight,
    mobileWidth,
    query = MEDIA.LG,
    skeletonSx,
    skeletonVariant,
    ...sizeProps
}: ImageProps) {
    const { border, isDarkMode } = useAppPalette();

    const [loaded, setLoaded] = useState(() => typeof src === "string" && loadedSrcs.has(src));
    const [errored, setErrored] = useState(false);
    const prevSrc = useRef(src);
    if (prevSrc.current !== src) {
        prevSrc.current = src;
        setLoaded(typeof src === "string" && loadedSrcs.has(src));
        setErrored(false);
    }

    let width: string | undefined = undefined;
    let height: string | undefined = undefined;
    let maxWidth: string | undefined = undefined;

    if (variant === "fixed-height") {
        const props = sizeProps as ImageWithHeight;
        height = props.height;
        width = props.width ?? "auto";
    } else if (variant === "fixed-width") {
        const props = sizeProps as ImageWithWidth;
        width = props.width;
        height = props.height ?? "auto";
        maxWidth = "max-width";
    } else {
        const props = sizeProps as ImageWithSize;
        width = props.size;
        height = props.size;
    }

    if (loading || !src || errored) {
        const skeletonAndNormalSx = { ...sx, ...skeletonSx } as SxType;
        return (
            <Skeleton
                variant={skeletonVariant ?? (variant === "circle" ? "circular" : "rectangular")}
                sx={{
                    opacity: hideSkeleton ? 0 : 1,
                    [query.below]: {
                        minHeight: mobileHeight,
                        height: mobileHeight,
                        minWidth: mobileWidth,
                        width: mobileWidth,
                        ...mobileSx
                    },
                    minHeight: height,
                    minWidth: width,
                    width: width,
                    height: height,
                    borderRadius: variant === "rounded" ? BORDER_RADIUS : undefined,
                    ...skeletonAndNormalSx
                }}
            />
        );
    }

    const skeletonColor = isDarkMode ? "rgba(255, 255, 255, 0.13)" : "rgba(0, 0, 0, 0.11)";
    const showSkeletonBg = !loaded;

    return (
        <StyledImage
            src={src}
            loading="lazy"
            decoding="async"
            width={width?.endsWith("px") ? parseInt(width) : undefined}
            height={height?.endsWith("px") ? parseInt(height) : undefined}
            onLoad={() => {
                // Skip data: URLs (large base64 would leak) and cap the set size.
                if (typeof src === "string" && !src.startsWith("data:")) {
                    loadedSrcs.add(src);
                    if (loadedSrcs.size > 1000) {
                        const oldest = loadedSrcs.values().next().value;
                        if (oldest !== undefined) loadedSrcs.delete(oldest);
                    }
                }
                setLoaded(true);
            }}
            onError={() => setErrored(true)}
            onContextMenu={doNothing} // prevent image saving on android
            sx={{
                overflow: "hidden",
                border: showBorder ? border : undefined,
                borderRadius: variant === "circle" ? "100%" : variant === "rounded" ? BORDER_RADIUS : undefined,
                objectFit: ["fixed-height", "fixed-width"].includes(variant) ? "contain" : "cover",
                width,
                backgroundColor: showSkeletonBg ? skeletonColor : undefined,
                animation: showSkeletonBg ? `${pulse} 1.5s ease-in-out infinite` : undefined,
                [query.below]: {
                    minHeight: mobileHeight,
                    height: mobileHeight,
                    minWidth: mobileWidth,
                    width: mobileWidth,
                    ...mobileSx
                },
                minWidth: width,
                minHeight: height,
                maxWidth,
                height,
                // prevent image saving on safari
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
                ...sx
            }}
        />
    );
}

export type OverlappingImagesProps = {
    images: string[] | { src: string; variant: ImageVariant }[] | undefined;
    overlap?: number;
    size: string;
    sx?: SxType;
    maxLength?: number;
    showPlus?: boolean;
    hideRow?: boolean;
    plusVariant?: TextVariant;
    outline?: boolean;
};

export function OverlappingImages({
    images,
    size,
    sx,
    maxLength = 3,
    showPlus = true,
    hideRow,
    plusVariant = "body2",
    overlap,
    outline
}: OverlappingImagesProps) {
    if (!images) {
        return <Image sx={sx} size={size} variant="circle" src={undefined} />;
    }

    const sources = images.slice(0, maxLength);

    const content = (
        <>
            <OverlappingImagesBase images={sources} size={size} sx={sx} overlap={overlap} outline={outline} />
            {images.length > maxLength && showPlus && (
                <Text variant={plusVariant} sx={{ ml: 0.5 }} color="disabled">
                    +{images.length - maxLength}
                </Text>
            )}
        </>
    );

    if (hideRow) return content;

    return <Row>{content}</Row>;
}

export function OverlappingImagesBase({
    images,
    sx,
    size,
    overlap,
    outline = true
}: Pick<OverlappingImagesProps, "images" | "size" | "sx" | "overlap" | "outline">) {
    const tokenOverlap = (overlap !== undefined ? -1 * overlap : (-1 * parseInt(size)) / 2) / SPACING;
    const { background } = useAppPalette();

    return (
        <>
            {images?.map((image, i) => {
                const src = typeof image === "string" ? image : image?.src;
                const variant: ImageVariant = typeof image === "string" ? "circle" : image.variant;

                return (
                    <Image
                        sx={{
                            ml: i === 0 ? 0 : tokenOverlap,
                            zIndex: images?.length - i,
                            ...sx,
                            border: outline === true && i < images?.length - 1 ? `2px solid ${background}` : undefined
                        }}
                        size={size}
                        variant={variant}
                        key={i}
                        src={src}
                    />
                );
            })}
        </>
    );
}
